import React, { useEffect, useState } from "react";
import axios from "axios";
import { Avatar, Badge, Button, Edit05, Eye, Spinner } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import TemplateHTMLPreview from "../../BeefreeTemplates/TemplateHTMLPreview";
import { toastError } from "../../../../shared/toastHelper";

const moment = require("moment-timezone");

const styles = {
  pane: {
    maxWidth: 560,
    margin: "0 auto",
    padding: 32,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 600,
    color: "var(--sl-text-secondary)",
  },
  card: {
    background: "var(--sl-bg-primary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "var(--sl-radius-xl)",
    boxShadow: "var(--sl-shadow-sm)",
    overflow: "hidden",
  },
  toLine: {
    padding: "12px 16px",
    fontSize: 13,
    color: "var(--sl-text-tertiary)",
    borderBottom: "1px solid var(--sl-border-secondary)",
  },
  inboxRow: {
    display: "flex",
    gap: 12,
    padding: 16,
  },
  inboxText: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    lineHeight: "20px",
  },
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  emptyDesign: {
    padding: 32,
    textAlign: "center",
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
  designFrame: {
    border: "none",
    width: "100%",
    height: 640,
  },
};

export const audienceSummary = (broadcast, hasRecipients) => {
  if (!hasRecipients) return "No one yet";

  const engaged = broadcast.target_engaged_audience ? " · engaged only" : "";

  if (broadcast.recipient_type === "all_fans") return `Everyone in the workspace${engaged}`;

  if (broadcast.segment_type === "new") return `${broadcast.segment?.name || "Custom conditions"}${engaged}`;

  const labels = (broadcast.parent_segment_filter?.segments || []).map((segment) => segment.label);
  const named = labels.slice(0, 2).join(" or ");
  const more = labels.length > 2 ? ` + ${labels.length - 2} more` : "";

  return `${named || "Custom conditions"}${more}${engaged}`;
};

const InboxPreview = ({ hasRecipients, onEditDesign }) => {
  const {
    csrfToken,
    team,
    venueOwnership,
    broadcast,
    subject,
    preheader,
    emailSendDomain,
    recipientsCount,
    scheduledSend,
    scheduledAt,
    template,
    sampleData,
    beefreeTemplate,
    templateHasContent,
  } = useBroadcastEditContext();

  const [designHtml, setDesignHtml] = useState(null);
  const [designError, setDesignError] = useState(null);

  useEffect(() => {
    if (!templateHasContent) return undefined;

    let active = true;
    setDesignHtml(null);
    setDesignError(null);

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/beefree_templates/${beefreeTemplate.id}/render_liquid_html`, {
      beefree_template: {
        template,
        sample_data: sampleData,
        venue_ownership_id: venueOwnership.id,
      },
    })
      .then(({ data }) => {
        if (!active) return;
        if (!data.parsedText) {
          setDesignError(data.liquidErrors?.[0] || "We couldn't render this design.");
          return;
        }
        setDesignHtml(data.parsedText);
      })
      .catch(() => {
        if (!active) return;
        setDesignError("We couldn't render this design.");
        toastError("Could not load the email preview, please try again");
      });

    return () => {
      active = false;
    };
  }, [templateHasContent, template, sampleData]);

  const senderName = emailSendDomain?.name || venueOwnership.venueable.name;
  const sendTime = scheduledSend && scheduledAt ? moment(scheduledAt).format("MMM D") : "Now";

  return (
    <div style={styles.pane}>
      <div style={styles.header}>
        <Eye width={16} height={16} />
        Inbox preview
        <Badge size="sm" color="gray">{hasRecipients || recipientsCount === "--" ? recipientsCount : 0} fans</Badge>
        <span style={{ flex: 1 }} />
        {templateHasContent && (
          <Button variant="link-color" size="sm" icon={Edit05} onClick={onEditDesign}>
            Edit design
          </Button>
        )}
      </div>

      <div style={styles.card}>
        <div style={styles.toLine}>To: {audienceSummary(broadcast, hasRecipients)}</div>
        <div style={styles.inboxRow}>
          <Avatar size="md" initials={senderName.charAt(0).toUpperCase()} alt={senderName} />
          <div style={styles.inboxText}>
            <div style={{ display: "flex", gap: 8 }}>
              <strong style={{ ...styles.truncate, flex: 1, color: "var(--sl-text-primary)" }}>{senderName}</strong>
              <span style={{ fontSize: 12, color: "var(--sl-text-tertiary)" }}>{sendTime}</span>
            </div>
            <div style={{ ...styles.truncate, color: subject ? "var(--sl-text-primary)" : "var(--sl-text-quaternary)" }}>
              {subject || "Your subject line"}
            </div>
            <div style={{ ...styles.truncate, color: preheader ? "var(--sl-text-tertiary)" : "var(--sl-text-quaternary)" }}>
              {preheader || "Preview text shows here"}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        {!templateHasContent ? (
          <div style={styles.emptyDesign}>Your email design shows up here once you build it in Content.</div>
        ) : designError ? (
          <div style={styles.emptyDesign}>{designError} Check it in Content.</div>
        ) : designHtml === null ? (
          <div style={styles.emptyDesign}><Spinner size="sm" aria-label="Loading email preview" /></div>
        ) : (
          <TemplateHTMLPreview htmlContent={designHtml} style={styles.designFrame} disableLinks />
        )}
      </div>
    </div>
  );
};

export default InboxPreview;
